import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../AxiosInstance';
import { Toast, Modal } from './v2-utils';
import '../styles/v2.css';

const today = () => new Date().toISOString().split('T')[0];
const genTxnId = () => `TXN-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

function emptyItem() {
  return { product: '', productName: '', productDetails: { product_name: '', variety: '', brand: '', size: '' }, unitTypes: [], unitId: '', price: '', quantity: '', date: today(), total: 0, currentStockDisplay: '' };
}

function formatProductDisplay(p) {
  let s = p.product_name;
  if (p.brand) s += ` (${p.brand})`;
  const vs = [p.variety, p.size || p.description].filter(Boolean);
  if (vs.length) s += ` - ${vs.join(', ')}`;
  return s;
}

/* Single item row component */
function ItemRow({ item, index, searchResults, onProductNameChange, onProductNameBlur, onProductNameFocus, onSelectProduct, onFieldChange, onUnitChange, onDelete, canDelete }) {
  return (
    <div style={{ background: '#fafbfa', borderRadius: 14, padding: 16, border: '1px solid #f1f5f1', display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Row header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Item {index + 1}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: '#065f46' }}>K {Number(item.total || 0).toFixed(2)}</span>
          {canDelete && (
            <button type="button" onClick={() => onDelete(index)}
              style={{ width: 28, height: 28, borderRadius: 8, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ width: 13, height: 13 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* Product search */}
      <div style={{ position: 'relative' }}>
        <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input className="v2-input" style={{ paddingLeft: 36 }} type="text" placeholder="Search product…"
          value={item.productName}
          onChange={e => onProductNameChange(index, e.target.value)}
          onBlur={() => onProductNameBlur(index)}
          onFocus={() => onProductNameFocus(index)}
          autoComplete="off" required />
        {item.product && (
          <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg style={{ width: 11, height: 11, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
          </div>
        )}
        {searchResults?.length > 0 && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto' }}>
            {searchResults.map((p, i) => (
              <button key={i} type="button"
                onMouseDown={e => { e.preventDefault(); onSelectProduct(index, p); }}
                style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '10px 14px', background: 'none', border: 'none', borderBottom: i < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{p.product_name}</span>
                <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{[p.brand && `Brand: ${p.brand}`, p.variety && `Variety: ${p.variety}`, p.size && `Size: ${p.size}`].filter(Boolean).join(' · ') || 'No details'}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Unit + Price + Qty */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <div style={{ position: 'relative' }}>
          <select className="v2-input" style={{ paddingRight: 28, appearance: 'none', cursor: 'pointer', fontSize: 13 }}
            value={item.unitId} onChange={e => onUnitChange(index, e.target.value)} required disabled={!item.product}>
            <option value="">Unit…</option>
            {item.unitTypes.map(u => <option key={u.id} value={u.id}>{u.type}</option>)}
          </select>
          <svg style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
        </div>
        <input className="v2-input" type="number" placeholder="Price (K)" style={{ fontSize: 13 }}
          value={item.price} onChange={e => onFieldChange(index, 'price', e.target.value)} min="0" step="0.01" required />
        <input className="v2-input" type="number" placeholder="Qty" style={{ fontSize: 13 }}
          value={item.quantity} onChange={e => onFieldChange(index, 'quantity', e.target.value)} min="0" step="1" required />
      </div>

      {/* Stock + Date */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ padding: '10px 12px', borderRadius: 10, background: '#f8fafc', border: '1.5px solid #e2e8f0', fontSize: 12, color: '#64748b' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', display: 'block', marginBottom: 2 }}>STOCK</span>
          {item.unitId ? (item.currentStockDisplay || 'Loading…') : 'Select unit'}
        </div>
        <input className="v2-input" type="date" style={{ fontSize: 13 }}
          value={item.date} onChange={e => onFieldChange(index, 'date', e.target.value)} required />
      </div>
    </div>
  );
}

export default function TransactionV2() {
  const [items, setItems]               = useState([emptyItem()]);
  const [discount, setDiscount]         = useState(0);
  const [discountFocused, setDiscountFocused] = useState(false);
  const [grandTotal, setGrandTotal]     = useState(0);
  const [searchResults, setSearchResults] = useState({});
  const [isOnline, setIsOnline]         = useState(navigator.onLine);
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [txnId, setTxnId]               = useState(genTxnId);
  const [toast, setToast]               = useState(null);
  const [loading, setLoading]           = useState(false);
  const [inventoryLink, setInventoryLink] = useState(null);

  const justSelectedRef = useRef({});
  const blurTimeoutRef  = useRef({});

  useEffect(() => {
    const up = () => setIsOnline(true), down = () => setIsOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up); window.removeEventListener('offline', down);
      Object.values(blurTimeoutRef.current).forEach(t => t && clearTimeout(t));
    };
  }, []);

  /* grand total */
  useEffect(() => {
    const sub = items.reduce((s, item) => s + parseFloat(item.total || 0), 0);
    setGrandTotal(Math.max(0, sub - parseFloat(discount || 0)));
  }, [items, discount]);

  /* product search per row */
  useEffect(() => {
    const doSearch = async () => {
      const results = {};
      for (let i = 0; i < items.length; i++) {
        const q = items[i].productName;
        if (q.length > 2 && isOnline && !justSelectedRef.current[i]) {
          try {
            const res = await axiosInstance.get(`/products/search?q=${q}`);
            const unique = Object.values(res.data.reduce((acc, p) => { const k=`${p.product_name}-${p.variety||''}-${p.brand||''}-${p.size||''}`; if(!acc[k]) acc[k]=p; return acc; }, {}));
            results[i] = unique;
          } catch { results[i] = []; }
        } else { results[i] = []; }
        if (justSelectedRef.current[i]) justSelectedRef.current[i] = false;
      }
      setSearchResults(results);
    };
    doSearch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map(it => it.productName).join('|'), isOnline]);

  /* handlers */
  const updateItem = (index, patch) => setItems(prev => { const next=[...prev]; next[index]={...next[index],...patch}; return next; });

  const handleProductNameChange = (index, val) => {
    updateItem(index, { productName: val, product: '', productDetails: { product_name:'',variety:'',brand:'',size:'' }, unitTypes:[], unitId:'', price:'', total:0, currentStockDisplay:'' });
  };

  const handleProductNameBlur = (index) => {
    blurTimeoutRef.current[index] = setTimeout(() => setSearchResults(p => ({...p,[index]:[]})), 300);
  };

  const handleProductNameFocus = (index) => {
    if (blurTimeoutRef.current[index]) { clearTimeout(blurTimeoutRef.current[index]); blurTimeoutRef.current[index]=null; }
  };

  const handleSelectProduct = async (index, product) => {
    if (blurTimeoutRef.current[index]) { clearTimeout(blurTimeoutRef.current[index]); blurTimeoutRef.current[index]=null; }
    justSelectedRef.current[index] = true;
    setSearchResults(p => ({...p,[index]:[]}));

    const patch = {
      product: String(product.product_id),
      productName: formatProductDisplay(product),
      productDetails: { product_name: product.product_name, variety: product.variety||'', brand: product.brand||'', size: product.size||'' },
      price: '', unitId: '', unitTypes: [], total: 0, currentStockDisplay: '',
    };

    try {
      const unitsRes = await axiosInstance.get(`/units/product/${product.product_id}`);
      const selling = unitsRes.data.filter(u => u.unit_category==='selling').map(u => ({ id: u.unit_id, type: u.unit_type, category: u.unit_category }));
      patch.unitTypes = selling;
      if (selling.length === 1) {
        patch.unitId = selling[0].id;
      }
    } catch {}

    updateItem(index, patch);

    if (patch.unitId) {
      await fetchPrice(index, patch.unitId, String(product.product_id), patch);
    }
  };

  const fetchPrice = async (index, unitId, productId, basePatch = null) => {
    try {
      const res = await axiosInstance.get(`/sales/price-suggestions/${productId}/${unitId}`);
      const price = res.data.has_price_history ? String(res.data.suggested_retail_price) : '';
      const stockRes = await axiosInstance.get(`/inventories/product/${productId}/unit/${unitId}`).catch(()=>({data:{}}));
      const stockVal = Number(stockRes.data.current_stock??0);
      const stockDisplay = `${Number.isFinite(stockVal)?stockVal.toFixed(2):'-'}${stockRes.data.unit_type?' '+stockRes.data.unit_type:''}`;

      setItems(prev => {
        const next=[...prev];
        const cur=next[index];
        const p=parseFloat(price||cur.price||0), q=parseFloat(cur.quantity||0);
        next[index]={...cur,...(basePatch||{}), price, currentStockDisplay:stockDisplay, total:p*q };
        return next;
      });
    } catch {}
  };

  const handleUnitChange = async (index, unitId) => {
    updateItem(index, { unitId });
    const pid = items[index].product;
    if (pid) await fetchPrice(index, unitId, pid);
  };

  const handleFieldChange = (index, field, val) => {
    setItems(prev => {
      const next=[...prev];
      next[index]={...next[index],[field]:val};
      if (field==='price'||field==='quantity') {
        const p=parseFloat(field==='price'?val:next[index].price||0);
        const q=parseFloat(field==='quantity'?val:next[index].quantity||0);
        next[index].total=p*q;
      }
      return next;
    });
  };

  const handleDeleteItem = (index) => {
    if (items.length <= 1) { setToast({ message:'Cannot delete the last item. Use Reset instead.', severity:'warning' }); return; }
    if (blurTimeoutRef.current[index]) { clearTimeout(blurTimeoutRef.current[index]); }
    setItems(prev => prev.filter((_,i)=>i!==index));
    setSearchResults(prev => { const n={}; Object.keys(prev).forEach(k=>{ const ki=parseInt(k); if(ki<index)n[ki]=prev[k]; else if(ki>index)n[ki-1]=prev[k]; }); return n; });
  };

  const handleReset = () => {
    Object.values(blurTimeoutRef.current).forEach(t=>t&&clearTimeout(t));
    blurTimeoutRef.current={}; justSelectedRef.current={};
    setItems([emptyItem()]); setDiscount(0); setGrandTotal(0); setTxnId(genTxnId()); setSearchResults({}); setInventoryLink(null);
  };

  const handleConfirmSales = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setInventoryLink(null);

    const subtotal = items.reduce((s,it)=>s+parseFloat(it.total||0),0);

    for (const [i, item] of items.entries()) {
      if (!item.product||!item.unitId||!item.price||!item.quantity) {
        setToast({ message:`Item ${i+1} is missing required fields.`, severity:'error' }); setLoading(false); return;
      }
      const proportion = subtotal>0?(parseFloat(item.total||0)/subtotal):0;
      const itemDiscount = parseFloat(discount||0)*proportion;
      const saleData = {
        product_name: item.productDetails.product_name,
        variety: item.productDetails.variety,
        brand: item.productDetails.brand,
        size: item.productDetails.size||'',
        retail_price: item.price,
        quantity: item.quantity,
        sale_date: item.date,
        unit_id: item.unitId,
        unit_category: item.unitTypes.find(u=>u.id===item.unitId)?.category,
        trans_id: txnId,
        discount: itemDiscount.toFixed(2),
      };
      try {
        await axiosInstance.post('/sales', saleData);
      } catch (err) {
        const msg = err.response?.data?.error||err.message||'';
        if (msg.includes('Inventory not found')||msg.includes('add the item to inventory')||msg.includes('not found for product')) {
          setInventoryLink({ href:`/new/dashboard/inventories/add?product_id=${item.product}&product_name=${encodeURIComponent(item.productDetails.product_name)}&variety=${encodeURIComponent(item.productDetails.variety)}`, label:`Set stock for ${item.productDetails.product_name}`, type:'setup' });
          setToast({ message:`${msg} — set up inventory first.`, severity:'error' });
        } else if (msg.includes('Insufficient stock')) {
          setInventoryLink({ href:'/new/dashboard/inventories/view', label:'Reconcile inventory', type:'reconcile' });
          setToast({ message:`${msg} for ${item.productDetails.product_name}.`, severity:'error' });
        } else {
          setToast({ message:`Failed for item ${i+1}: ${msg}`, severity:'error' });
        }
        setLoading(false); return;
      }
    }

    setToast({ message:`Transaction completed! ID: ${txnId}`, severity:'success' });
    handleReset();
    setLoading(false);
  };

  const subtotal = items.reduce((s,it)=>s+parseFloat(it.total||0),0);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div>
        <h1 style={{ color: '#0f172a', fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Customer Transaction</h1>
        <p style={{ color: '#94a3b8', fontSize: 13, margin: '4px 0 0' }}>Add multiple items to a single transaction</p>
      </div>

      {/* Items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item, i) => (
          <ItemRow key={i} item={item} index={i}
            searchResults={searchResults[i]}
            onProductNameChange={handleProductNameChange}
            onProductNameBlur={handleProductNameBlur}
            onProductNameFocus={handleProductNameFocus}
            onSelectProduct={handleSelectProduct}
            onFieldChange={handleFieldChange}
            onUnitChange={handleUnitChange}
            onDelete={handleDeleteItem}
            canDelete={items.length > 1} />
        ))}
      </div>

      {/* Add item */}
      <button type="button" onClick={() => setItems(prev=>[...prev,emptyItem()])}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', borderRadius: 12, border: '1.5px dashed #a7f3d0', background: '#f0fdf4', color: '#065f46', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
        <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
        Add Another Item
      </button>

      {/* Totals card */}
      <div className="v2-card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: '#475569', flexShrink: 0 }}>Overall Discount (K):</label>
            <input className="v2-input" type="number" placeholder="0.00"
              value={discountFocused && discount===0 ? '' : discount}
              onChange={e => setDiscount(Math.max(0, parseFloat(e.target.value)||0))}
              onFocus={() => setDiscountFocused(true)}
              onBlur={() => setDiscountFocused(false)}
              min="0" step="0.01"
              style={{ width: 130 }} />
          </div>
          {discount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Subtotal</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>K {subtotal.toFixed(2)}</span>
            </div>
          )}
          {discount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Discount</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>− K {parseFloat(discount).toFixed(2)}</span>
            </div>
          )}
          <div style={{ height: 1, background: '#f1f5f1' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Grand Total</span>
            <span style={{ fontSize: 24, fontWeight: 900, color: '#065f46', letterSpacing: '-0.03em' }}>K {grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Inventory error link */}
      {inventoryLink && (
        <div style={{ padding: '14px 18px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca' }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: '#b91c1c', fontWeight: 500 }}>Action required before completing transaction:</p>
          <a href={inventoryLink.href} style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700, textDecoration: 'underline' }}>{inventoryLink.label} →</a>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={handleReset}
          style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          Reset
        </button>
        <button type="button" onClick={() => setConfirmOpen(true)} disabled={loading}
          className="v2-btn-submit" style={{ flex: 2, fontSize: 15, padding: '12px 0' }}>
          {loading ? 'Processing…' : 'Submit Transaction'}
        </button>
      </div>

      {/* Confirmation modal */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Confirm Transaction"
        footer={
          <>
            <button onClick={() => setConfirmOpen(false)} style={{ padding:'9px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'white', color:'#475569', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button onClick={handleConfirmSales} style={{ padding:'9px 20px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#10b981,#059669)', color:'white', fontSize:13, fontWeight:700, cursor:'pointer' }}>Confirm & Log Sales</button>
          </>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((it, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, background: '#f8fafc', border: '1px solid #f1f5f1' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.productName || '—'}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>
                  {it.unitTypes.find(u=>u.id===it.unitId)?.type||'—'} · K {it.price} × {it.quantity} · {it.date}
                </p>
              </div>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#065f46', flexShrink: 0, marginLeft: 12 }}>K {Number(it.total).toFixed(2)}</span>
            </div>
          ))}
          <div style={{ height: 1, background: '#f1f5f1', margin: '4px 0' }} />
          {discount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 14px' }}>
              <span style={{ fontSize: 13, color: '#64748b' }}>Discount</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>− K {parseFloat(discount).toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 14px' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Grand Total</span>
            <span style={{ fontSize: 18, fontWeight: 900, color: '#065f46' }}>K {grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
